import { initializeApp, getApps } from 'firebase/app'
import { 
  getAuth, 
  updatePassword, 
  EmailAuthProvider, 
  reauthenticateWithCredential 
} from 'firebase/auth'

/**
 * Serviço centralizado do Firebase
 * Gerencia autenticação e operações de senha
 */
class FirebaseService {
  constructor() {
    this.app = null
    this.auth = null
  }

  /**
   * Inicializa o Firebase (singleton)
   */
  init() {
    if (this.app) {
      return this.app
    }

    // Verificar se já existe uma instância inicializada
    const existingApps = getApps()
    if (existingApps.length > 0) {
      this.app = existingApps[0]
      this.auth = getAuth(this.app)
      return this.app
    }

    // Configuração Firebase do projeto meumentor-dev
    const firebaseConfig = {
      apiKey: 'AIzaSyDPdmR0PV7h0WRfBPGGPzJ6RQEacB1u8IM',
      authDomain: 'meumentor-dev.firebaseapp.com',
      projectId: 'meumentor-dev',
      storageBucket: 'meumentor-dev.appspot.com',
    }

    try {
      this.app = initializeApp(firebaseConfig)
      this.auth = getAuth(this.app)
      console.log('✅ Firebase Service inicializado')
      return this.app
    } catch (error) {
      console.error('❌ Erro ao inicializar Firebase Service:', error)
      throw error
    }
  }

  /**
   * Obtém a instância do Auth
   */
  getAuth() {
    if (!this.auth) {
      this.init()
    }
    return this.auth
  }

  /**
   * Reautentica o usuário com senha atual
   * @param {string} currentPassword - Senha atual do usuário
   * @returns {Promise<void>}
   */
  async reauthenticate(currentPassword) {
    const auth = this.getAuth()
    const user = auth.currentUser

    if (!user || !user.email) {
      throw new Error('Usuário não autenticado')
    }

    const credential = EmailAuthProvider.credential(user.email, currentPassword)
    
    try {
      await reauthenticateWithCredential(user, credential)
      console.log('✅ Reautenticação realizada com sucesso')
    } catch (error) {
      console.error('❌ Erro na reautenticação:', error)
      
      // Traduzir erros do Firebase
      if (error.code === 'auth/wrong-password') {
        throw new Error('Senha atual incorreta')
      } else if (error.code === 'auth/too-many-requests') {
        throw new Error('Muitas tentativas. Tente novamente mais tarde')
      } else if (error.code === 'auth/user-not-found') {
        throw new Error('Usuário não encontrado')
      }
      
      throw new Error('Erro ao verificar senha atual')
    }
  }

  /**
   * Atualiza a senha do usuário
   * @param {string} currentPassword - Senha atual
   * @param {string} newPassword - Nova senha
   * @returns {Promise<void>}
   */
  async changePassword(currentPassword, newPassword) {
    // 1. Reautenticar com senha atual
    await this.reauthenticate(currentPassword)

    // 2. Atualizar senha
    const auth = this.getAuth()
    const user = auth.currentUser

    if (!user) {
      throw new Error('Usuário não autenticado')
    }

    try {
      await updatePassword(user, newPassword)
      console.log('✅ Senha atualizada com sucesso')
    } catch (error) {
      console.error('❌ Erro ao atualizar senha:', error)
      
      // Traduzir erros do Firebase
      if (error.code === 'auth/weak-password') {
        throw new Error('A nova senha é muito fraca. Use pelo menos 6 caracteres')
      } else if (error.code === 'auth/requires-recent-login') {
        throw new Error('Por segurança, faça login novamente antes de alterar a senha')
      }
      
      throw new Error('Erro ao atualizar senha')
    }
  }

  /**
   * Valida força da senha
   * @param {string} password - Senha a ser validada
   * @returns {Object} { valid: boolean, errors: string[] }
   */
  validatePasswordStrength(password) {
    const errors = []

    if (!password) {
      errors.push('Senha obrigatória')
      return { valid: false, errors }
    }

    if (password.length < 8) {
      errors.push('Mínimo de 8 caracteres')
    }

    if (!/[A-Z]/.test(password)) {
      errors.push('Pelo menos uma letra maiúscula')
    }

    if (!/[a-z]/.test(password)) {
      errors.push('Pelo menos uma letra minúscula')
    }

    if (!/[0-9]/.test(password)) {
      errors.push('Pelo menos um número')
    }

    if (!/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password)) {
      errors.push('Pelo menos um caractere especial')
    }

    return {
      valid: errors.length === 0,
      errors,
    }
  }
}

// Exportar instância singleton
export default new FirebaseService()
